/**
 * The studio's menu as data, with no JSX: every door (its route, its English label, its group and the flag it hides
 * behind), the desktop rail's order, and the phone's bottom bar and More drawer. SideMenu and MobileNav draw it with
 * their own icons. FTUX - Getting Started reads it for its menu map, the rail's light and the phone map, so a door
 * added, moved, renamed or put behind a flag here reaches all three (.claude/rules/ftux.md).
 *
 * It imports nothing but a type: SideMenu and MobileNav import the FTUX's targets, and the FTUX imports this.
 */
import type { FeatureFlagKey } from '../../config/featureFlags';

/** The groups the menu is told in, top to bottom: the rail sets each one apart from the one above it. */
export const STUDIO_MENU_GROUPS = ['overview', 'growth', 'content', 'account'] as const;
export type StudioMenuGroup = (typeof STUDIO_MENU_GROUPS)[number];

/** One door of the studio's menu. */
export type StudioDoor = {
  href: string;
  /** Its English label as the rail and the bar show it (translated where it renders). */
  label: string;
  group: StudioMenuGroup;
  /** The feature flag it is shown behind (absent: always shown). */
  flag?: FeatureFlagKey;
};

const DOORS = {
  home: { href: '/account/home', label: 'Home', group: 'overview' },
  // Pulse is no longer a door of its own: it is a tab of Stats (/account/pulse redirects to Stats on that tab).
  stats: { href: '/account/stats', label: 'Stats', group: 'overview' },
  players: { href: '/account/players', label: 'Players', group: 'overview' },
  product: { href: '/account/product', label: 'Product', group: 'growth' },
  acquisition: { href: '/account/marketing', label: 'Acquisition', group: 'growth' },
  operations: { href: '/account/operations', label: 'Operations', group: 'growth' },
  // Website: what the owner shows on their casino site (Website Creatives, Logos, Art Style, Game Lists, Blog). Ungated,
  // the areas gate themselves; /account/website is the hub with `website-hub` on.
  website: { href: '/account/website', label: 'Website', group: 'content' },
  // Marketing Assets: generally available, kept behind `creatives-assets` as a rollback switch.
  marketing: { href: '/account/marketing-assets', label: 'Marketing', group: 'content', flag: 'creatives-assets' },
  // Wallet: on desktop the top bar's balance pill is its door, so it stays off the rail.
  wallet: { href: '/account/wallet', label: 'Wallet', group: 'account' },
  settings: { href: '/account/settings', label: 'Settings', group: 'account' },
} as const satisfies Readonly<Record<string, StudioDoor>>;

export type StudioDoorKey = keyof typeof DOORS;

/** Every door, by its key. */
export const STUDIO_DOORS: Readonly<Record<StudioDoorKey, StudioDoor>> = DOORS;

/** The desktop rail, top to bottom. Its overflow tucks the last ones into More first, so the first ones stay longest. */
export const RAIL_DOORS = [
  'home',
  'stats',
  'players',
  'product',
  'acquisition',
  'operations',
  'website',
  'marketing',
  'settings',
] as const satisfies readonly StudioDoorKey[];
export type RailDoorKey = (typeof RAIL_DOORS)[number];

/**
 * How the rail sets a group apart from the one above it: a separator line, or (the account) floated to the rail's foot
 * when the rail is full height.
 */
export const RAIL_GROUP_BREAK: Readonly<Record<StudioMenuGroup, 'line' | 'foot'>> = {
  overview: 'line',
  growth: 'line',
  content: 'line',
  account: 'foot',
};

/** The phone's bottom bar, left to right; its More button follows them. */
export const PHONE_BAR_DOORS = ['home', 'stats', 'marketing', 'website'] as const satisfies readonly StudioDoorKey[];
export type PhoneBarDoorKey = (typeof PHONE_BAR_DOORS)[number];

/** The phone's More drawer, top to bottom. */
export const PHONE_DRAWER_DOORS = [
  'product',
  'acquisition',
  'operations',
  'players',
  'wallet',
  'settings',
] as const satisfies readonly StudioDoorKey[];
export type PhoneDrawerDoorKey = (typeof PHONE_DRAWER_DOORS)[number];

/** What the rail's and the bar's More buttons say (their Text literal, kept for the extractor). */
export const MENU_MORE_LABEL = 'More';

/** The doors the Newton showcase (`selectNShowcase`) leaves out: it shows no owner wallet. The phone's More drawer
 *  (MobileNav) and the FTUX's phone map read this one list. */
export const SHOWCASE_HIDDEN_DOORS: readonly StudioDoorKey[] = ['wallet'];

/** Whether a door is shown under the flags `isEnabled` reads. */
export const isDoorShown = (key: StudioDoorKey, isEnabled: (flag: FeatureFlagKey | undefined) => boolean): boolean =>
  isEnabled(STUDIO_DOORS[key].flag);
